import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useCFAuth } from '@/lib/auth/cf-auth-context';
import {
  cacheSavedDriveAttachment,
  clearPendingDriveSave,
  getPendingDriveSave,
  saveAttachmentToDrive,
} from '@/lib/client/drive';

function clearDriveStatusFromUrl(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('drive')) return;

  url.searchParams.delete('drive');
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', nextUrl);
}

export function DriveSaveResume() {
  const { status, user } = useCFAuth();
  const isHandlingRef = useRef(false);

  useEffect(() => {
    const driveStatus = new URL(window.location.href).searchParams.get('drive');
    if (!driveStatus || isHandlingRef.current) {
      return;
    }

    if (driveStatus === 'connected') {
      if (status !== 'authenticated' || !user?.id || !user.driveConnected) {
        return;
      }

      const pendingSave = getPendingDriveSave();
      if (!pendingSave) {
        toast.success('Google Drive connected');
        clearDriveStatusFromUrl();
        return;
      }

      isHandlingRef.current = true;
      void (async () => {
        try {
          const result = await saveAttachmentToDrive(pendingSave);
          cacheSavedDriveAttachment(pendingSave, result);
          clearPendingDriveSave();
          toast.success(
            result.alreadySaved
              ? `${pendingSave.filename} is already in Drive`
              : `Saved ${pendingSave.filename} to Drive`,
            {
              action: result.webViewLink
                ? {
                    label: 'Open Drive',
                    onClick: () => window.open(result.webViewLink!, '_blank', 'noopener,noreferrer'),
                  }
                : undefined,
            }
          );
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to save attachment to Drive');
        } finally {
          clearDriveStatusFromUrl();
          isHandlingRef.current = false;
        }
      })();
      return;
    }

    if (driveStatus === 'cancelled') {
      toast.error('Google Drive connection was cancelled');
      clearDriveStatusFromUrl();
      return;
    }

    if (driveStatus === 'auth_required') {
      toast.error('Sign in again to connect Google Drive');
      clearDriveStatusFromUrl();
      return;
    }

    if (driveStatus === 'error') {
      toast.error('Failed to connect Google Drive');
      clearDriveStatusFromUrl();
    }
  }, [status, user?.id, user?.driveConnected]);

  return null;
}
